import { useQuery } from '@tanstack/react-query';
import {
  snapshotRangesValid,
  type SnapshotRangeState,
} from '@/components/snapshot/SnapshotDateRangePicker';
import {
  EMPTY_RETAIL_KPI_METRICS,
  type WorkspaceRetailKpiMetrics,
} from '@/utils/workspaceAdProductMetrics';
import { adsApi, type WorkspaceRetailKpiPeriod } from '@/api/client';

function periodToRetail(
  period: WorkspaceRetailKpiPeriod | undefined
): WorkspaceRetailKpiMetrics {
  if (!period?.mapped) return EMPTY_RETAIL_KPI_METRICS;
  return {
    totalSales: period.totalSales,
    organicSales: period.organicSales,
    totalOrders: period.totalOrders,
    organicOrders: period.organicOrders,
    tacos: period.tacos,
    glanceViews: period.glanceViews ?? 0,
  };
}

export function useWorkspaceRetailKpis(
  profileId: string,
  rangeState: SnapshotRangeState,
  options?: { enabled?: boolean }
) {
  const rangesOk = snapshotRangesValid(rangeState);
  const enabled = Boolean(profileId) && rangesOk && (options?.enabled ?? true);

  const query = useQuery({
    queryKey: [
      'workspace-retail-kpis',
      profileId,
      rangeState.current.startDate,
      rangeState.current.endDate,
      rangeState.compareEnabled ? rangeState.previous.startDate : '',
      rangeState.compareEnabled ? rangeState.previous.endDate : '',
      rangeState.compareEnabled,
    ],
    queryFn: async () => {
      const res = await adsApi.getRetailKpis(profileId, {
        startDate: rangeState.current.startDate,
        endDate: rangeState.current.endDate,
        ...(rangeState.compareEnabled
          ? {
              compareStartDate: rangeState.previous.startDate,
              compareEndDate: rangeState.previous.endDate,
            }
          : {}),
      });
      if (!res.success || !res.data) throw new Error('Failed to load retail KPIs');
      return res.data;
    },
    enabled,
    staleTime: 60_000,
  });

  return {
    current: periodToRetail(query.data?.current),
    previous: periodToRetail(
      rangeState.compareEnabled ? query.data?.previous : undefined
    ),
    dataThrough: query.data?.dataThrough ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}
